/* eslint-disable @typescript-eslint/no-explicit-any */
import kebabCase from 'lodash/kebabCase';
import {memo, useEffect, useMemo, useRef, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {Card, Grid, Link, Skeleton, Typography} from '@mui/material';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {metaInfo} from '@configs';
import {CATEGORY, JOBFEED, RECRUITER_JOBFEED} from '@constants';
import {usePageLoad} from '@hooks';
import {Box} from '@jobseeker/lotus/foundations';
import {JobFeed, NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp, isRecruiterStale, isUserAgentForBot} from '@utils';
import {getListSEOContent, getPageUrl} from '@utils/seo';
import dayjs from 'dayjs';

import {loadSEO} from '@helpers';
// `wrapper` from the /stores subpath, not the root `@jobseeker/common-core`
// barrel (which re-exports the entire package surface into this chunk).
import {wrapper} from '@jobseeker/common-core/stores';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {
  gladiatorService,
  jobfeedService,
} from '@jobseeker/common-core/services';
// Direct subpath instead of the utils barrel, which pulls crypto-js/jsencrypt.
import {
  handleTracking,
  TrackEventUBA,
} from '@jobseeker/common-core/utils/analytics';
import {CTA, UserProfileCard} from '@jobseeker/lotus/components';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import type {GetServerSidePropsContext} from 'next/types';
import {useRouter} from 'next/router';
import {FaLinkedin} from 'react-icons/fa';

import type {AppState} from '@jobseeker/common-core/types';
import Header from '@jobseeker/feature-job/components/Recruiter/Header';
import dynamic from 'next/dynamic';
import cookie from 'cookie';
import NotFoundPage from '../404';
import {COOKIE_LOGIN_AUTH} from '@jobseeker/common-core/constants';

// Floating chat widget: client-only, below the fold, and not SEO-relevant.
// Load it lazily so it stays out of the recruiter route's initial JS chunk.
const Chat = dynamic(() => import('@components/Chat'), {ssr: false});

const AdsPanelHarness = memo(
  dynamic(
    () => import('@jobseeker/client-harness/components/AdsHarness/AdsPanelV2'),
    {ssr: false}
  )
);

// ── Skeleton components ────────────────────────────────────────────────────

const IimjobsProfileSkeleton = () => (
  <Card sx={{borderRadius: 2, overflow: 'hidden', mb: 2}}>
    <Skeleton variant="rectangular" height={90} sx={{bgcolor: 'grey.300'}} />
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{mt: -3.5, px: 2, pb: 2}}
    >
      <Skeleton variant="circular" width={60} height={60} sx={{mb: 1}} />
      <Skeleton width="65%" height={18} sx={{mb: 0.5}} />
      <Skeleton width="80%" height={14} sx={{mb: 2}} />
    </Box>
    <Box px={2} pb={2}>
      <Skeleton width="50%" height={14} sx={{mb: 0.5}} />
      <Skeleton width="65%" height={14} sx={{mb: 1.5}} />
      <Skeleton width="40%" height={14} sx={{mb: 0.5}} />
      <Skeleton width="55%" height={14} sx={{mb: 1.5}} />
      <Skeleton width="45%" height={14} sx={{mb: 0.75}} />
      {[1, 2, 3].map(i => (
        <Skeleton key={i} width="80%" height={14} sx={{mb: 0.5}} />
      ))}
    </Box>
  </Card>
);

const HiristRecruiterShimmer = () => (
  <Box p={{xxs: 2, md: 0}}>
    <Card variant="outlined" sx={{mb: 2}}>
      <Box
        sx={{
          bgcolor: '#13355c',
          backgroundImage:
            "url('https://job-static.hirist.com/V8/static/media/background-bg.ba8365c7.webp')",
          backgroundRepeat: 'no-repeat',
          minHeight: {xxs: '250px', md: '210px', lg: '240px'},
          display: 'flex',
          flexDirection: {xxs: 'column', md: 'row'},
          alignItems: 'center',
          justifyContent: {xxs: 'center', md: 'flex-start'},
          padding: {xxs: '2px 0px', md: '2px 6px'},
        }}
      >
        <Box
          sx={{
            width: {xxs: '100%', md: '10%'},
            display: 'flex',
            justifyContent: {xxs: 'center', md: 'flex-start'},
            mt: {xxs: '40px', md: 0},
            ml: {lg: '20px'},
          }}
        >
          <Skeleton
            variant="circular"
            width={80}
            height={80}
            sx={{bgcolor: 'rgba(255,255,255,0.2)'}}
          />
        </Box>
        <Box sx={{width: {xxs: '100%', md: '85%'}, px: {xxs: 2, md: 1}}}>
          <Skeleton
            height={28}
            width="40%"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              mt: {xxs: 1.5, md: 0},
              mb: 0.5,
            }}
          />
          <Skeleton
            height={18}
            width="55%"
            sx={{bgcolor: 'rgba(255,255,255,0.2)', mb: 0.5}}
          />
          <Skeleton
            height={16}
            width="25%"
            sx={{bgcolor: 'rgba(255,255,255,0.2)', mb: 1.5}}
          />
          <Skeleton
            height={1}
            width="100%"
            sx={{bgcolor: 'rgba(255,255,255,0.15)', mb: 1}}
          />
          <Skeleton
            height={14}
            width="32%"
            sx={{bgcolor: 'rgba(255,255,255,0.15)', mb: 1}}
          />
          <Box display="flex" gap={1} flexWrap="wrap">
            {[70, 45, 60, 50, 85, 48, 68, 52, 58, 72].map((w, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={26}
                width={w}
                sx={{bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '20px'}}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Card>
    <Grid container spacing={1}>
      <Grid
        item
        xxs={14}
        md={7.75}
        lg={7.9}
        sx={{
          mx: {xxs: 4, md: 6.75, lg: 9.5},
          mr: {xxs: 1, md: 3, lg: 3.5},
          my: {xxs: 4.5, md: 5.5, lg: 5.5},
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{mb: 1.5}}>
          Jobs Posted
        </Typography>
      </Grid>
    </Grid>
  </Box>
);

// ── Helper sub-components ──────────────────────────────────────────────────

const TruncatedTags = ({data, truncateSize = 8}: any) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <>
      {data
        ?.slice(0, isExpanded ? data?.length : truncateSize)
        ?.map((value: {tagId: number; tagName: string}, index: number) => (
          <Link
            sx={{
              textDecoration: 'none',
              color: 'text.primary',
              ':hover': {color: 'primary.main'},
            }}
            key={`rec-tag-${index}-${value.tagId}`}
            href={`/k/${kebabCase(value.tagName)}-jobs`}
          >
            &nbsp;#{value.tagName}
          </Link>
        ))}
      {data?.length > truncateSize && (
        <Typography
          color={'primary'}
          variant="subtitle3"
          sx={{cursor: 'pointer'}}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <br />
          See {!isExpanded ? 'More' : 'Less'}
        </Typography>
      )}
    </>
  );
};

const TruncatedText = ({data, truncateSize}: any) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <>
      {data?.slice(0, isExpanded ? data?.length : truncateSize)}
      {data?.length > truncateSize && (
        <>
          {!isExpanded && '...'}
          <Typography
            color={'primary'}
            variant="subtitle3"
            sx={{cursor: 'pointer'}}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            &nbsp; &nbsp;See {!isExpanded ? 'More' : 'Less'}
          </Typography>
        </>
      )}
    </>
  );
};

// ── Page component ─────────────────────────────────────────────────────────

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const RecruiterPage = ({
  userIdSSR,
  filters,
  recruiterSSRData,
  recruiterId,
  url,
  isStale,
  seoConfig,
}: {
  userIdSSR: number;
  slug: string;
  filters: any;
  recruiterSSRData: any;
  recruiterId: any;
  url: string;
  isStale: boolean;
  seoConfig: any;
}) => {
  const appDomain = process.env.NEXT_PUBLIC_APP_DOMAIN;
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, RECRUITER_JOBFEED);
  const isLarge = useIsLargeDevice();

  usePageLoad(RECRUITER_JOBFEED);

  const dispatch = useDispatch<any>();
  const [recruiterData, setRecruiterData] = useState<any>(recruiterSSRData);
  const [recruiterLoading, setRecruiterLoading] = useState(!recruiterSSRData);
  const [recruiterNotFound, setRecruiterNotFound] = useState(false);
  const [localIsStale, setLocalIsStale] = useState(isStale);

  useValidateAuth(dispatch);

  const router = useRouter();
  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);
  const [pageNumber, setPageNumber] = useState(filters?.page + 1);
  const hasTracked = useRef(false);
  const initialPageTracked = useRef(false);
  const hasPageNoChanged = useRef(false);

  useEffect(() => {
    hasPageNoChanged.current = true;
  }, [pageNumber]);

  useEffect(() => {
    if (hasTracked.current && !hasPageNoChanged.current) return;
    const actionType: string =
      pageNumber > filters.page + 1
        ? 'scroll'
        : initialPageTracked.current
        ? 'scroll'
        : 'view';

    hasTracked.current = true;
    hasPageNoChanged.current = false;

    TrackEventUBA('recruiterProfilePageView', actionType, {
      ref: router.query.ref || '',
      pageName: 'Recruiter ProfilePage',
      pageNo: pageNumber,
      recruiterID: Number(recruiterId) || '',
      sessionUsername: userIdSSR || userId || '-1',
    });
    initialPageTracked.current = true;
  }, [userId, pageNumber]);

  useEffect(() => {
    if (!isAppIimjobs) {
      handleTracking('Recruiter profile View', {
        event_category: 'Offline Landing page',
        Type: 'view',
      });
    }
  }, []);

  useEffect(() => {
    if (recruiterData) return;
    jobfeedService
      .getRecruiterData(recruiterId)
      .then(data => {
        if (!data?.id) {
          setRecruiterNotFound(true);
        } else {
          setRecruiterData(data);
          setLocalIsStale(isRecruiterStale(data?.lastJobPostedDate));
        }
        setRecruiterLoading(false);
      })
      .catch(err => {
        console.error(err);
        setRecruiterLoading(false);
      });
  }, [recruiterId]);

  const recruiterCardData = useMemo(() => {
    if (!recruiterData) return [];
    let data: any = [];
    if (recruiterData.about) {
      data.push({
        title: 'About',
        subTitle: (
          <TruncatedText
            truncateSize={isLarge ? 150 : 180}
            data={recruiterData.about}
          />
        ),
      });
    }
    data = data.concat([
      {
        title: (
          <>
            Last Active:{' '}
            <Typography
              component={'span'}
              fontWeight={500}
              color={'text.primary'}
              fontSize={12}
            >
              {dayjs(recruiterData.lastActive).isValid()
                ? dayjs(recruiterData.lastActive).format('DD MMM YYYY')
                : recruiterData.lastActive}
            </Typography>
          </>
        ),
      },
      {
        title: (
          <>
            Location:{' '}
            <Typography
              component={'span'}
              fontWeight={500}
              color={'text.primary'}
              fontSize={12}
            >
              {recruiterData.location || 'N/A'}
            </Typography>
          </>
        ),
      },
      {
        title: 'Hiring for skills',
        subTitle: <TruncatedTags data={recruiterData?.recruiterTags} />,
      },
    ]);
    return data;
  }, [recruiterData]);

  const seoString = useMemo(() => {
    if (!recruiterData) return '';
    return `${recruiterData.designation} at ${recruiterData.organisation} - ${recruiterData.name} ${appDomain}`;
  }, [recruiterData]);

  const seoDescriptionString =
    recruiterData && Array.isArray(recruiterData.recruiterTags)
      ? `${recruiterData?.name}, ${recruiterData?.designation} at ${recruiterData?.organisation} is hiring for multiple job vacancies in ${recruiterData?.recruiterTags[0]?.tagName}, ${recruiterData?.recruiterTags[1]?.tagName}, ${recruiterData?.recruiterTags[2]?.tagName} etc - ${appDomain}`
      : '';

  const breadcrumbElements: any = isAppIimjobs
    ? [{name: 'Home', item: '/'}, {name: "Recruiter's Page"}]
    : [];

  const theme = useWrappedTheme();

  if (recruiterNotFound) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Interceptor metaInfo={metaData}>
        {(seoString || Object.keys(seoConfig as object).length > 0) && (
          <SeoLayout
            seoConfig={{
              ...(seoConfig as object),
              title: seoString,
              description: seoDescriptionString,
              canonical: `https://www.${appDomain}${url}`,
              noindex: localIsStale,
              nofollow: localIsStale,
            }}
            paginationEnabled={true}
          />
        )}
        <NavLayout>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={breadcrumbElements}
          >
            {isAppIimjobs ? (
              <Box p={{xxs: 2, md: 0}}>
                <Grid container spacing={2}>
                  {/* Left: profile card */}
                  <Grid item xxs={12} sm={3} md={3} lg={2.3} xl={2}>
                    {recruiterLoading ? (
                      <IimjobsProfileSkeleton />
                    ) : (
                      <>
                        <UserProfileCard
                          variant={VersionVariants.VERSION_TWO}
                          mode={'RECRUITER'}
                          subtitle={`${recruiterData?.designation} at ${recruiterData?.organisation}`}
                          name={
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              justifyContent="center"
                              fontSize={theme.typography.pxToRem(14)}
                              fontWeight={600}
                            >
                              {recruiterData?.name}
                              {recruiterData?.linkedInUrl && (
                                <Link
                                  href={recruiterData?.linkedInUrl}
                                  target="_blank"
                                  lineHeight={0}
                                >
                                  <FaLinkedin color="#0077B5" size={16} />
                                </Link>
                              )}
                            </Box>
                          }
                          profileImgUrl={
                            recruiterData?.image || recruiterData?.logoLink
                          }
                        />
                        <Box mt={2}>
                          <CTA
                            variant={VersionVariants.VERSION_SIX}
                            cardInfo={recruiterCardData}
                          />
                        </Box>
                      </>
                    )}
                  </Grid>
                  {/* Right: job listings */}
                  <Grid item xxs={12} md sx={{maxWidth: '810px !important'}}>
                    <JobFeed
                      jobFeedType={RECRUITER_JOBFEED}
                      catOrTagId={recruiterId}
                      catOrTagTitle={seoString || ''}
                      showProfileCard={false}
                      showFeatured={false}
                      showLearning={false}
                      showLearningCenterCTA={false}
                      preFilters={filters}
                      setPageNumber={setPageNumber}
                      feedOnly={true}
                      enablePagination={true}
                    />
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <>
                {recruiterLoading ? (
                  <HiristRecruiterShimmer />
                ) : (
                  <>
                    <Header recruiterData={recruiterData} />
                    <Grid container spacing={1}>
                      <Grid
                        item
                        xxs={14}
                        md={7.75}
                        lg={7.9}
                        sx={{
                          mx: {xxs: 4, md: 6.75, lg: 9.5},
                          mr: {xxs: 1, md: 3, lg: 3.5},
                          my: {xxs: 4.5, md: 5.5, lg: 5.5},
                        }}
                      >
                        <JobFeed
                          jobFeedType={RECRUITER_JOBFEED}
                          catOrTagId={recruiterId}
                          catOrTagTitle={seoString}
                          showProfileCard={false}
                          showFeatured={true}
                          showLearning={true}
                          showLearningCenterCTA={false}
                          preFilters={filters}
                          setPageNumber={setPageNumber}
                          feedOnly={true}
                          enablePagination={true}
                        />
                      </Grid>
                      <Grid
                        item
                        xxs={2.75}
                        lg={2.5}
                        sx={{
                          mx: {md: 1, lg: 2.5},
                          my: {md: 7.25, lg: 7},
                          display: {xxs: 'none', md: 'block'},
                        }}
                      >
                        <AdsPanelHarness
                          variant={VersionVariants.VERSION_TWO}
                          showSmallBadges={false}
                          showFeatured={true}
                          showLearning={true}
                          showLearningCenterCTA={false}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </>
            )}
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

// ── Server-side props ──────────────────────────────────────────────────────

export const getServerSideProps = wrapper.getServerSideProps(
  (store: {dispatch: (arg0: {type: string; payload: any}) => void}) =>
    async (context: GetServerSidePropsContext) => {
      const pageNo = parseInt(context.query?.page) || 0;
      const minExp = parseInt(context.query?.minexp) || 0;
      const maxExp = parseInt(context.query?.maxexp) || 0;
      const query = parseInt(context.query?.maxexp) || 0;
      const sort = context.query?.sort || null;
      const url = context?.resolvedUrl;

      let userId = -1;
      const ssrHeaders = {
        'user-agent': context.req?.headers?.['user-agent'],
        'x-forwarded-for':
          context.req?.headers?.['x-forwarded-for']
            ?.toString()
            .split(',')[0]
            .trim() ||
          context.req?.socket?.remoteAddress ||
          '',
        referer: `https://www.${process.env.NEXT_PUBLIC_APP_DOMAIN}/`,
      };

      try {
        if (context.req?.headers?.cookie) {
          const cookies = cookie.parse(context.req.headers.cookie);
          if (cookies?.[COOKIE_LOGIN_AUTH]) {
            const validateInfo = await gladiatorService.validate('validate', {
              headers: {
                cookie: context.req?.headers?.cookie,
                ...ssrHeaders,
              },
            });
            userId = parseInt(validateInfo?.userId ?? '0', 10);
          }
        }
      } catch (err) {}

      const isBotUser = isUserAgentForBot(context);
      const recruiterId = parseInt(
        (context.params?.slug as string).split('-')[0]
      );

      const filters = {
        sort: sort ? [sort] : null,
        page: pageNo > 0 ? pageNo - 1 : 0,
        query: query,
        minexp: minExp,
        maxexp: maxExp,
      };

      store.dispatch({
        type: 'AppContext/set_current_page',
        payload: {page: RECRUITER_JOBFEED},
      });
      store.dispatch({type: 'AppContext/set_slug', payload: ''});

      if (!isBotUser) {
        return {
          props: {
            userIdSSR: userId,
            slug: context.params?.slug as string,
            filters,
            seoConfig: {},
            recruiterSSRData: null,
            recruiterId,
            url,
            isStale: false,
          },
        };
      }

      const recruiterSSRData = await jobfeedService.getRecruiterData(
        recruiterId,
        ssrHeaders
      );
      if (!recruiterSSRData.id) {
        return {notFound: true};
      }

      const recSSrJobs: any =
        (await jobfeedService.getRecuiterJobFeed({
          userId: String(userId),
          recruiterId: recruiterId.toString(),
          pageNo: pageNo > 0 ? pageNo - 1 : 0,
          headers: ssrHeaders,
        })) || [];

      store.dispatch({type: 'job/jobFeed/set_jobfeed', payload: recSSrJobs});

      const seoTitle = `${recruiterSSRData?.name || ''} at ${
        recruiterSSRData?.organisation || ''
      }`;
      const seoConfig = await loadSEO(
        CATEGORY,
        getListSEOContent(seoTitle, pageNo, recSSrJobs?.totalJobs ?? 0),
        getPageUrl(context?.resolvedUrl || '', pageNo),
        true,
        pageNo
      );

      return {
        props: {
          userIdSSR: userId,
          slug: context.params.slug,
          filters,
          seoConfig,
          recruiterSSRData,
          recruiterId,
          url,
          isStale: isRecruiterStale(recruiterSSRData?.lastJobPostedDate),
        },
      };
    }
);

export default RecruiterPage;
